// Who is signed in, as the app sees it. Everything reads this interface, not
// the OIDC library, so tests and the visitor's read-only view (O4) need no
// Keycloak. Roles gate what the UI offers; the API decides what is allowed.
import { createContext, useContext } from 'react';

export const ASSEMBLY_ROLE = 'assembly';
export const DATA_RESET_ROLE = 'data-reset';

export interface Session {
    /** Whether sign-in state is still being read. */
    readonly loading: boolean;
    /** Whether someone is signed in. */
    readonly signedIn: boolean;
    /** Their sign-in name. */
    readonly username?: string;
    /** The name shown for them. */
    readonly displayName?: string;
    /** Their realm roles, from the access token. */
    readonly roles: readonly string[];
    /** The current access token, for the API client. */
    readonly accessToken?: string;
    /** Starts sign-in, returning here afterwards. */
    readonly signIn: () => void;
    /** Signs out. */
    readonly signOut: () => void;
}

export const anonymous: Session = {
    loading: false,
    signedIn: false,
    roles: [],
    signIn: () => undefined,
    signOut: () => undefined,
};

export const SessionContext = createContext<Session>(anonymous);

export const useSession = (): Session => useContext(SessionContext);

/** Whether the caller may build: the Technician persona, or admin (D2). */
export const canBuild = (session: Session): boolean => session.roles.includes(ASSEMBLY_ROLE);

/** Whether the caller may reset demo data (ADR-0043). */
export const canReset = (session: Session): boolean => session.roles.includes(DATA_RESET_ROLE);

/** The `roles` claim of an access token, read without verifying it: for the UI only. */
export function rolesOf(accessToken: string | undefined): string[] {
    const payload = accessToken?.split('.')[1];
    if (!payload) return [];
    try {
        const json = atob(
            payload
                .replace(/-/g, '+')
                .replace(/_/g, '/')
                .padEnd(Math.ceil(payload.length / 4) * 4, '='),
        );
        const claims = JSON.parse(json) as { roles?: unknown };
        return Array.isArray(claims.roles)
            ? claims.roles.filter((role): role is string => typeof role === 'string')
            : [];
    } catch {
        return [];
    }
}

/** What the shop floor asks Keycloak to hand back with a sign-in: where to return to. */
export interface ReturnState {
    readonly returnTo?: string;
}

/**
 * Where to go once signed in: the path the sign-in started from, if it is one
 * of this app's own (never another origin, and never the callback itself),
 * else the floor.
 */
export function returnPathOf(state: unknown): string {
    const returnTo = (state as ReturnState | null | undefined)?.returnTo;
    return typeof returnTo === 'string' &&
        returnTo.startsWith('/') &&
        !returnTo.startsWith('//') &&
        !returnTo.startsWith('/auth/callback')
        ? returnTo
        : '/floor';
}
